import { useMemo } from 'react';
import { Skeleton } from 'antd';
import { useDateFormatter } from 'shared/hooks/dates.hooks';
import { Tabs } from 'src/components/design-system';
import { NotFound } from 'src/components/common';
import { useQualityMetricsContext, TAB_KEYS } from '../../quality-metrics-context';
import { OverviewTab } from '../overview-tab/overview-tab';
import { CasesTab } from '../cases-tab/cases-tab';
import { ResourcesTab } from '../resources-tab/resources-tab';
import { DefinitionTab } from '../definition-tab/definition-tab';
import styles from './metric-detail-panel.module.scss';

export function MetricDetailPanel() {
  const {
    qualityMetric,
    userMetric,
    loadingDetail,
    errorDetail,
    selectedTab,
    setSelectedTab,
    showCasesTab,
    processedUserTrends,
    lang,
  } = useQualityMetricsContext();
  const { formatLongDate } = useDateFormatter();

  const hasMetrics = !!(qualityMetric && userMetric);

  const tabItems = useMemo(() => {
    if (!hasMetrics) return [];

    const items = [
      {
        key: TAB_KEYS.OVERVIEW,
        label: lang.TAB_OVERVIEW,
        children: <OverviewTab />,
      },
    ];

    if (showCasesTab) {
      items.push({
        key: TAB_KEYS.CASES,
        label: lang.TAB_CASES,
        children: <CasesTab />,
      });
    }

    if (qualityMetric?.resources?.length) {
      items.push({
        key: TAB_KEYS.RESOURCES,
        label: lang.TAB_RELATED_RESOURCES,
        children: <ResourcesTab />,
      });
    }

    if (qualityMetric?.numerator || qualityMetric?.denominator) {
      items.push({
        key: TAB_KEYS.DEFINITION,
        label: lang.TAB_DEFINITION,
        children: <DefinitionTab />,
      });
    }

    return items;
  }, [hasMetrics, qualityMetric, showCasesTab, lang]);

  if (loadingDetail) {
    return (
      <div className={styles.panel}>
        <Skeleton active title={{ width: '40%' }} paragraph={false} />
        <Skeleton active title={false} paragraph={{ rows: 8, width: '100%' }} />
      </div>
    );
  }

  if (errorDetail || !hasMetrics) {
    return (
      <div className={styles.panel}>
        <NotFound />
      </div>
    );
  }

  const firstTrend = processedUserTrends?.[0];
  const lastTrend = processedUserTrends?.[processedUserTrends.length - 1];

  const startDateFormatted = firstTrend
    ? formatLongDate(new Date(firstTrend.year, firstTrend.month - 1))
    : qualityMetric?.startDate
      ? formatLongDate(qualityMetric.startDate)
      : null;

  const endDateFormatted = lastTrend
    ? formatLongDate(new Date(lastTrend.year, lastTrend.month - 1))
    : null;

  const dateRangeLabel =
    startDateFormatted && endDateFormatted
      ? `${startDateFormatted} – ${endDateFormatted}`
      : startDateFormatted;

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <h2 className={styles.title}>{qualityMetric.title}</h2>
          {dateRangeLabel && <span className={styles.dateRange}>{dateRangeLabel}</span>}
        </div>
      </div>

      <Tabs activeKey={selectedTab} onChange={setSelectedTab} items={tabItems} destroyOnHidden />
    </div>
  );
}
